import { Controller } from "@hotwired/stimulus"

// Controlador simplificado para refunds - siempre por items
export default class extends Controller {
  static targets = [
    "itemCheckbox",
    "itemQuantity",
    "itemSubtotal",
    "selectedTotal",
    "hiddenFields",
    "exceedsAlert",
    "submitButton",
    "deliveryNote",
    "adjustmentToggle",
    "adjustmentSection",
    "adjustmentInput",
    "adjustmentError"
  ]

  static values = {
    maxAmount: Number,
    deliveryFee: { type: Number, default: 0 },
    deliveryTip: { type: Number, default: 0 }
  }

  connect() {
    this.maxAmount = this.maxAmountValue || 0
    this.disableSubmit()
  }

  // Link "Marcar todos"
  selectAll(event) {
    event.preventDefault()
    this.itemCheckboxTargets.forEach(checkbox => {
      if (!checkbox.disabled) {
        checkbox.checked = true
        this.toggleQuantityInput(checkbox, true)
      }
    })
    this.calculateTotal()
  }

  // Cuando el usuario marca/desmarca un checkbox
  itemChanged(event) {
    const checkbox = event.target
    this.toggleQuantityInput(checkbox, checkbox.checked)
    this.calculateTotal()
  }

  // Cuando el usuario cambia la cantidad
  quantityChanged() {
    this.calculateTotal()
  }

  toggleQuantityInput(checkbox, setMaxValue = false) {
    const itemId = checkbox.dataset.itemId
    const quantityInput = this.itemQuantityTargets.find(
      input => input.dataset.itemId === itemId
    )
    if (quantityInput) {
      quantityInput.disabled = !checkbox.checked
      if (checkbox.checked && setMaxValue) {
        quantityInput.value = quantityInput.max
      }
    }
  }

  calculateTotal() {
    let total = 0

    this.itemCheckboxTargets.forEach((checkbox, index) => {
      if (checkbox.checked) {
        const price = parseFloat(checkbox.dataset.price) || 0
        const itemId = checkbox.dataset.itemId
        const quantityInput = this.itemQuantityTargets.find(
          input => input.dataset.itemId === itemId
        )
        const quantity = quantityInput ? parseInt(quantityInput.value) || 0 : 0
        const subtotal = price * quantity

        total += subtotal

        // Update subtotal badge
        if (this.itemSubtotalTargets[index]) {
          this.itemSubtotalTargets[index].textContent = `$${subtotal.toLocaleString('es-CL')}`
        }
      } else {
        // Reset subtotal badge when unchecked
        if (this.itemSubtotalTargets[index]) {
          const price = parseFloat(checkbox.dataset.price) || 0
          const max = parseInt(checkbox.dataset.max) || 0
          this.itemSubtotalTargets[index].textContent = `$${(price * max).toLocaleString('es-CL')}`
        }
      }
    })

    // Si todos los items están seleccionados al máximo → incluir delivery + tip
    const allSelected = this.allItemsFullySelected()
    let displayTotal = total
    if (allSelected && (this.deliveryFeeValue > 0 || this.deliveryTipValue > 0)) {
      displayTotal += this.deliveryFeeValue + this.deliveryTipValue
      this.showDeliveryNote()
    } else {
      this.hideDeliveryNote()
    }

    // Adjustment amount — clamp a 0 si es negativo o NaN
    const adjustmentValue = this.readAdjustmentValue()
    displayTotal += adjustmentValue

    this.selectedTotalTarget.textContent = `$${displayTotal.toLocaleString('es-CL')}`

    // Validate total vs available
    if (displayTotal > this.maxAmount) {
      this.showExceedsAlert()
      this.disableSubmit()
    } else if (displayTotal > 0) {
      this.hideExceedsAlert()
      this.enableSubmit()
    } else {
      this.hideExceedsAlert()
      this.disableSubmit()
    }

    this.updateHiddenFields()
  }

  // Lee el adjustment clampeando negativos a 0 y tratando input vacio como 0
  readAdjustmentValue() {
    if (!this.hasAdjustmentInputTarget) return 0

    const raw = parseFloat(this.adjustmentInputTarget.value)
    if (isNaN(raw)) {
      this.hideAdjustmentError()
      return 0
    }
    if (raw < 0) {
      this.adjustmentInputTarget.value = ''
      this.showAdjustmentError()
      return 0
    }
    this.hideAdjustmentError()
    return raw
  }

  toggleAdjustment(event) {
    event.preventDefault()
    if (!this.hasAdjustmentSectionTarget) return

    const hidden = this.adjustmentSectionTarget.hasAttribute('hidden')
    if (hidden) {
      this.adjustmentSectionTarget.removeAttribute('hidden')
      this.adjustmentInputTarget?.focus()
      if (this.hasAdjustmentToggleTarget) this.adjustmentToggleTarget.classList.add('expanded')
    } else {
      this.adjustmentSectionTarget.setAttribute('hidden', '')
      if (this.adjustmentInputTarget) this.adjustmentInputTarget.value = ''
      if (this.hasAdjustmentToggleTarget) this.adjustmentToggleTarget.classList.remove('expanded')
      this.calculateTotal()
    }
  }

  showAdjustmentError() {
    if (this.hasAdjustmentErrorTarget) this.adjustmentErrorTarget.classList.remove('d-none')
  }

  hideAdjustmentError() {
    if (this.hasAdjustmentErrorTarget) this.adjustmentErrorTarget.classList.add('d-none')
  }

  showExceedsAlert() {
    if (this.hasExceedsAlertTarget) {
      this.exceedsAlertTarget.classList.remove("d-none")
    }
  }

  hideExceedsAlert() {
    if (this.hasExceedsAlertTarget) {
      this.exceedsAlertTarget.classList.add("d-none")
    }
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  updateHiddenFields() {
    this.clearHiddenFields()

    this.itemCheckboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        const itemId = checkbox.dataset.itemId
        const quantityInput = this.itemQuantityTargets.find(
          input => input.dataset.itemId === itemId
        )
        const quantity = quantityInput ? quantityInput.value : 1

        const idField = document.createElement('input')
        idField.type = 'hidden'
        idField.name = 'refund[items][][order_item_id]'
        idField.value = itemId

        const qtyField = document.createElement('input')
        qtyField.type = 'hidden'
        qtyField.name = 'refund[items][][quantity]'
        qtyField.value = quantity

        this.hiddenFieldsTarget.appendChild(idField)
        this.hiddenFieldsTarget.appendChild(qtyField)
      }
    })
  }

  clearHiddenFields() {
    this.hiddenFieldsTarget.innerHTML = ''
  }

  allItemsFullySelected() {
    // Sin items refundables (ej: todos ya refundados en refunds previos) este flujo no aplica.
    // Evita que el JS auto-sume delivery+tip bloqueando refunds solo-adjustment.
    if (this.itemCheckboxTargets.length === 0) return false

    return this.itemCheckboxTargets.every(checkbox => {
      if (checkbox.disabled) return true // skip items that exceed available
      if (!checkbox.checked) return false
      const itemId = checkbox.dataset.itemId
      const quantityInput = this.itemQuantityTargets.find(i => i.dataset.itemId === itemId)
      if (!quantityInput) return false
      return parseInt(quantityInput.value) >= parseInt(quantityInput.max)
    })
  }

  showDeliveryNote() {
    if (this.hasDeliveryNoteTarget) this.deliveryNoteTarget.classList.remove('d-none')
  }

  hideDeliveryNote() {
    if (this.hasDeliveryNoteTarget) this.deliveryNoteTarget.classList.add('d-none')
  }
}
